import type { PatientEnCours } from "cadendar-shared";
import { pecMap, quoteMap, stateMap } from "./patientStateMaps.ts";

export type ActivePatientSortColumn =
  | "name"
  | "state"
  | "updatedAt"
  | "quoteState"
  | "pecState"
  | "nextRdv"
  | "comment";

export interface ActivePatientSort {
  column: ActivePatientSortColumn;
  direction: "asc" | "desc";
}

export interface SortableActivePatient {
  id: string;
  name: string;
  forename: string;
  state: PatientEnCours["state"];
  quote_state: PatientEnCours["quote_state"];
  pec_state: PatientEnCours["pec_state"];
  comment: string;
  updated_at: Date | null;
  next_rdv: {
    date: Date;
    hour: number;
    minute: number;
  } | null;
}

export const defaultActivePatientSort: ActivePatientSort = {
  column: "nextRdv",
  direction: "asc",
};

const collator = new Intl.Collator("fr", { sensitivity: "base" });

const getNextRdvTimestamp = (patient: SortableActivePatient) => {
  if (!patient.next_rdv) {
    return null;
  }

  return (
    patient.next_rdv.date.getTime() +
    (patient.next_rdv.hour * 60 + patient.next_rdv.minute) * 60 * 1000
  );
};

const getSortValue = (
  patient: SortableActivePatient,
  column: ActivePatientSortColumn,
) => {
  switch (column) {
    case "name":
      return `${patient.name} ${patient.forename}`;
    case "state":
      return stateMap[patient.state];
    case "updatedAt":
      return patient.updated_at?.getTime() ?? null;
    case "quoteState":
      return patient.quote_state ? quoteMap[patient.quote_state] : null;
    case "pecState":
      return patient.pec_state ? pecMap[patient.pec_state] : null;
    case "nextRdv":
      return getNextRdvTimestamp(patient);
    case "comment":
      return patient.comment || null;
  }
};

const compareValues = (
  left: string | number | null,
  right: string | number | null,
) => {
  if (typeof left === "string" && typeof right === "string") {
    return collator.compare(left, right);
  }
  return Number(left) - Number(right);
};

export const sortActivePatients = <T extends SortableActivePatient>(
  patients: readonly T[],
  sort: ActivePatientSort,
) =>
  [...patients].sort((left, right) => {
    const leftValue = getSortValue(left, sort.column);
    const rightValue = getSortValue(right, sort.column);

    if (leftValue === null) {
      return rightValue === null ? collator.compare(left.id, right.id) : 1;
    }
    if (rightValue === null) {
      return -1;
    }

    const comparison = compareValues(leftValue, rightValue);

    if (comparison !== 0) {
      return sort.direction === "asc" ? comparison : -comparison;
    }

    return collator.compare(left.id, right.id);
  });
